import { forwardRef, type ReactNode } from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { twMerge } from 'tailwind-merge'
import { tooltipArrowVariants, tooltipContentVariants } from './Tooltip.variants'
import { useTooltipTouch } from './hooks/useTooltipTouch'

export interface TooltipProps {
  trigger: ReactNode
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  delayDuration?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  ({ trigger, children, position = 'top', className, delayDuration = 200, open, onOpenChange }, ref) => {
    const isControlled = open !== undefined
    
    const {
      isTouchDevice,
      isOpen,
      handleOpenChange,
      handleTouchInteraction
    } = useTooltipTouch({
      isControlled,
      controlledOpen: open,
      onOpenChange
    })

    // For touch devices, use click behavior with proper positioning
    if (isTouchDevice) {
      return (
        <RadixTooltip.Provider>
          <RadixTooltip.Root onOpenChange={handleOpenChange} open={isOpen}>
            <RadixTooltip.Trigger asChild ref={ref}>
              <span
                aria-pressed={isOpen}
                className={twMerge('cursor-pointer inline-block', className)}
                data-tooltip-trigger
                onClick={handleTouchInteraction}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleOpenChange(!isOpen)
                  }
                }}
                onTouchEnd={handleTouchInteraction}
                role="button"
                tabIndex={0}
              >
                {trigger}
              </span>
            </RadixTooltip.Trigger>
            <RadixTooltip.Portal>
              <RadixTooltip.Content
                className={tooltipContentVariants({ position })}
                collisionPadding={8}
                data-radix-tooltip-content
                onEscapeKeyDown={() => { handleOpenChange(false) }}
                side={position}
                sideOffset={4}
              >
                {children}
                <RadixTooltip.Arrow className={tooltipArrowVariants({ position })} />
              </RadixTooltip.Content>
            </RadixTooltip.Portal>
          </RadixTooltip.Root>
        </RadixTooltip.Provider>
      )
    }

    // For non-touch devices, use normal hover behavior
    return (
      <RadixTooltip.Provider delayDuration={delayDuration} skipDelayDuration={300}>
        <RadixTooltip.Root onOpenChange={handleOpenChange} open={isOpen}>
          <RadixTooltip.Trigger asChild ref={ref}>
            <span className={twMerge('cursor-pointer inline-block', className)}>{trigger}</span>
          </RadixTooltip.Trigger>
          <RadixTooltip.Portal>
            <RadixTooltip.Content
              className={tooltipContentVariants({ position })}
              collisionPadding={8}
              data-radix-tooltip-content
              side={position}
              sideOffset={4}
            >
              {children}
              <RadixTooltip.Arrow className={tooltipArrowVariants({ position })} />
            </RadixTooltip.Content>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    )
  }
)

Tooltip.displayName = 'Tooltip'
